import { TextField, InputAdornment, IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

const toInteger = (rawValue, fallback = 0) => {
  const parsed = Number.parseInt(rawValue, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const clamp = (number, min, max) => {
  if (typeof min === "number" && number < min) return min;
  if (typeof max === "number" && number > max) return max;
  return number;
};

export const IntegerField = ({
  value,
  name,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  isDisabled = false,
  error = false,
  helperText = "",
}) => {
  const currentValue = value;
  const numericValue = clamp(toInteger(currentValue, min), min, max);

  const emitChange = (nextValue, shouldClamp = true) => {
    const resolvedValue = shouldClamp ? clamp(nextValue, min, max) : nextValue;

    if (typeof onChange === "function") {
      onChange(resolvedValue);
    }
  };

  const handleDecrement = () => {
    emitChange(numericValue - step);
  };

  const handleIncrement = () => {
    emitChange(numericValue + step);
  };

  const handleTextChange = (event) => {
    const onlyDigits = event.target.value.replace(/\D/g, "");

    if (onlyDigits === "") {
      emitChange(null, false);
      return;
    }

    emitChange(toInteger(onlyDigits, min));
  };

  return (
    <TextField
      value={currentValue ?? ""}
      name={name}
      onChange={handleTextChange}
      disabled={isDisabled}
      type="text"
      placeholder="0"
      fullWidth
      autoComplete="off"
      size="small"
      error={error}
      helperText={helperText}
      sx={{
        '& .MuiInputBase-root': {
          paddingLeft: '3px',
          paddingRight: '3px',
        },
        '& .MuiInputBase-input': {
          minWidth: 40,
          textAlign: 'center',
        },
      }}
      inputProps={{
        inputMode: "numeric",
        pattern: "[0-9]*",
        maxLength: 3,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ marginRight: 0 }}>
            <IconButton
              onClick={handleDecrement}
              edge="start"
              size="small"
              sx={{ padding: "4px" }}
              disabled={isDisabled || numericValue <= min}
              aria-label="decrement value"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" sx={{ marginLeft: 0 }}>
            <IconButton
              onClick={handleIncrement}
              edge="end"
              size="small"
              sx={{ padding: "4px" }}
              disabled={isDisabled || numericValue >= max}
              aria-label="increment value"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};
